import React, { PropsWithChildren, useEffect, useMemo, useState } from 'react'
import './GettingStarted.scss'
import { useGetPresetsQuery } from '../../store/ai/ai'
import { useAppSelector } from '../../store/hooks'
import { selectAccountId } from '../../store/auth/authSlice'
import { Accordion, AccordionItemProps, Col, Container, Row, Stack } from 'react-bootstrap'
import { metricsApi } from '../../store/metrics/metrics'
import classNames from 'classnames'
import { useDocumentTitle } from '../../common/useDocumentTitle'
import { Button } from '../../components/Button/Button'
import { useNavigate } from 'react-router-dom'
import { OnboardingTrackEvent, useTracking } from '../../apps/professional-app/fa/common/tracking'
import { useGettingStarted } from './hooks/useGettingStarted'
import { useGetMeQuery } from '../../store/user/user'
import { createState, IntegrationServiceId } from '../../common/integrations'
import { SelectDropdown } from '../../components/SelectDropdown/SelectDropdown'
import { ArcadeEmbed } from './ArcadeEmbed'
import { useIntegrations } from '../../apps/professional-app/fa/common/hooks/useIntegrations'
import { ROUTER_BASEPATH } from '../../common/constants'
import { IntegrationIcon } from '../../components/IntegrationIcon'

export type StepCircleProps = {
  finished?: boolean
}

export const StepCircle: React.FC<StepCircleProps & PropsWithChildren> = ({ finished = false, children }) => {
  return (
    <div className='step-circle-container'>
      <div className={classNames('step-circle rounded-circle border zocks-border-color-primary border-1 zocks-mb-1.5', { finished: finished })}>
        {finished && <i className='bi bi-check' />}
      </div>
      <div
        className={classNames('step-circle-text zocks-text-primary', {
          'zocks-text-success': finished,
        })}
      >
        {children}
      </div>
    </div>
  )
}

export type StepItemAccordionProps = {
  finished?: boolean
  headerText: string
} & PropsWithChildren &
  AccordionItemProps

export const StepItemAccordion: React.FC<StepItemAccordionProps> = ({ headerText, finished = false, children, eventKey }) => {
  return (
    <Accordion.Item
      eventKey={eventKey}
      className={classNames({
        finished: finished,
      })}
    >
      <Accordion.Header as='div'>
        <Stack direction='horizontal' gap={3} className='my-4'>
          <div className='step-circle-small rounded-circle border zocks-border-color-primary border-1'>{finished && <i className='bi bi-check' />}</div>
          <h5 className='medium'>{headerText}</h5>
        </Stack>
      </Accordion.Header>
      <Accordion.Body>{children}</Accordion.Body>
    </Accordion.Item>
  )
}

export const GettingStarted: React.FC = () => {
  useDocumentTitle('Getting Started')
  const accountId = useAppSelector(selectAccountId)
  const { calendarIntegrationOptions, calendarIntegrationOptionsByIntegration, isIntegrationAllowed } = useIntegrations()

  const { data: presets } = useGetPresetsQuery({ accountId, pageRequest: { page: 0, size: 1 } }, { refetchOnMountOrArgChange: true })
  const { data: userData, isSuccess: userDataSuccess } = useGetMeQuery({ accountId: accountId }, { refetchOnMountOrArgChange: true })
  const {
    gettingStartedEventsLoaded,
    isFinishedGettingStarted,
    isDemoMeetingViewedFinished,
    isZoomIntegrationFinished,
    isCalendarIntegrationFinished,
    isPresetsFinished,
    refetchEvents,
  } = useGettingStarted()

  const { trackingClient } = useTracking()
  const navigate = useNavigate()

  const [trigger] = metricsApi.endpoints?.getGettingStartedEventsSummary.useLazyQuery()
  useEffect(() => {
    if (!isPresetsFinished && presets && presets.data.length > 0) {
      updateFinishedSteps(0)
      trackingClient.track({
        eventName: OnboardingTrackEvent.ONBOARDING_PRESETS_DONE,
        eventDetails: {
          accountId: accountId,
        },
      })
    }
  }, [presets, isPresetsFinished])

  const [finishedSteps, setFinishedSteps] = useState<boolean[]>([])
  const [activeKey, setActiveKey] = useState('0')

  const zoomIntegration = useMemo(() => {
    if (userDataSuccess && userData.integrations?.length) {
      return userData.integrations?.some((i) => i.serviceId === IntegrationServiceId.ZOOM)
    }
    return false
  }, [userDataSuccess])

  const [selectedCalendarIntegration, setSelectedCalendarIntegration] = useState<IntegrationServiceId | null>(null)

  useEffect(() => {
    const finishedStepsList = [
      !!isPresetsFinished || !!presets?.data.length,
      !!isCalendarIntegrationFinished,
      !!isZoomIntegrationFinished || zoomIntegration,
      !!isDemoMeetingViewedFinished,
    ]
    setFinishedSteps(finishedStepsList)
  }, [isDemoMeetingViewedFinished, isZoomIntegrationFinished, isCalendarIntegrationFinished, isPresetsFinished, presets, zoomIntegration])

  useEffect(() => {
    const firstNonFinished = `${finishedSteps.findIndex((step) => !step)}`
    setActiveKey(firstNonFinished)
  }, [finishedSteps])

  const updateFinishedSteps = (step: number) => {
    setFinishedSteps((steps) =>
      steps.map((finished, i) => {
        if (i === step - 1) {
          return true
        } else {
          return finished
        }
      }),
    )
    refetchEvents()
  }

  const handlePreset = () => {
    navigate('/workspace/setup')
  }

  const handleZoomIntegration = () => {
    const path = window.location.origin + ROUTER_BASEPATH + '/getting-started?app=meeting'
    const state = createState(path)
    const redirectUrl = window.location.origin + ROUTER_BASEPATH + `/integrations/callback/zoom`
    const redirectUri = encodeURIComponent(redirectUrl)
    window.location.href = `${import.meta.env.REACT_APP_IAM_API_BASE_URL}/accounts/${accountId}/user-integrations/${IntegrationServiceId.ZOOM}/enable?redirect_uri=${redirectUri}&state=${state}`
  }

  const handleCalendarIntegration = (integrationType: IntegrationServiceId | null) => {
    if (!integrationType) return
    const path = window.location.origin + ROUTER_BASEPATH + '/getting-started'
    const state = createState(path)
    const redirectUrl = window.location.origin + ROUTER_BASEPATH + `/integrations/callback/${integrationType}`
    const redirectUri = encodeURIComponent(redirectUrl)
    window.location.href = `${import.meta.env.REACT_APP_IAM_API_BASE_URL}/accounts/${accountId}/user-integrations/${integrationType}/enable?redirect_uri=${redirectUri}&state=${state}`
  }

  const handleViewDemoMeeting = () => {
    trackingClient.track({ eventName: OnboardingTrackEvent.DEMO_MEETING_VIEWED, eventDetails: { source: 'gettingStarted' } })
  }

  const dismissOnboardingItem = (item: string, eventName: string, itemIndex: number) => {
    updateFinishedSteps(itemIndex)
    trackingClient.track({
      eventName: OnboardingTrackEvent.DISMISS_ONBOARDING_ITEM,
      eventDetails: {
        dismissed_item: item,
      },
    })
    trackingClient.track({
      eventName: eventName,
    })
  }

  const onDismissAll = () => {
    setFinishedSteps(Array(4).fill(true))
    trackingClient.track({
      eventName: OnboardingTrackEvent.ALL_DISMISSED,
    })
    trackingClient.track({
      eventName: OnboardingTrackEvent.DISMISS_ONBOARDING_ITEM,
      eventDetails: {
        dismissed_item: 'all',
      },
    })
    trigger({ accountId })
    navigate('/workspace/past-meetings')
  }

  return gettingStartedEventsLoaded ? (
    <>
      <Container fluid className='mb-6'>
        <Row className='zocks-mb-1.5'>
          <Col>
            <h2>Welcome to Zocks!</h2>
          </Col>
        </Row>
        <Row className='zocks-mb-1'>
          <Col>
            <p>Let’s make Zocks work for you. Follow these steps to get the most out of Zocks.</p>
          </Col>
        </Row>
        <Row className='position-relative step-circle-container'>
          <Col className='d-sm-none d-md-block d-none'></Col>
          {finishedSteps.map((finished: boolean, index: number) => {
            return (
              <Col key={`step-circle-${index}`}>
                <div className='circle-wrapper'>
                  <StepCircle key={`step_circle_${index}`} finished={finished}>
                    STEP {index + 1}
                  </StepCircle>
                </div>
              </Col>
            )
          })}
          <Col className='d-sm-none d-md-block d-none'></Col>
          <div className='faded-horizontal-rule position-absolute'></div>
        </Row>
      </Container>
      <Container fluid>
        <Row>
          <Col>
            <Accordion
              onSelect={(eventKey) => {
                setActiveKey(`${eventKey}`)
              }}
              activeKey={activeKey}
              flush
              className='onboarding-checklist'
            >
              <StepItemAccordion headerText='Capture data that’s most important for you' eventKey='0' finished={finishedSteps[0]}>
                <div>We have configured your account with meeting presets that captures data that’s tailored for you.</div>
                <Stack direction='horizontal' gap={3} className='my-4 justify-content-between'>
                  <Button variant='primary' onClick={handlePreset}>
                    Go to Presets
                  </Button>

                  {!finishedSteps[0] && (
                    <Button variant='link' onClick={() => dismissOnboardingItem('presets', OnboardingTrackEvent.ONBOARDING_PRESETS_DISMISSED, 1)}>
                      Skip for now
                    </Button>
                  )}
                </Stack>
              </StepItemAccordion>

              {calendarIntegrationOptions.length ? (
                <StepItemAccordion headerText='Manage your meetings in Zocks' eventKey='1' finished={finishedSteps[1]}>
                  <div>Zocks will sync your upcoming meetings so you have total control over how you want to process them.</div>
                  <Stack direction='horizontal' gap={3} className='my-4 justify-content-between'>
                    <Stack direction='horizontal' gap={2} className='w-50 justify-content-start'>
                      {isIntegrationAllowed(IntegrationServiceId.OUTLOOK_CALENDAR) ? (
                        <div className='rounded py-1 px-1 zocks-bg-color-gray-100'>
                          <IntegrationIcon serviceId={IntegrationServiceId.OUTLOOK_CALENDAR} />
                        </div>
                      ) : null}
                      {isIntegrationAllowed(IntegrationServiceId.GOOGLE_CALENDAR) ? (
                        <div className='rounded py-1 px-1 zocks-bg-color-gray-100'>
                          <IntegrationIcon serviceId={IntegrationServiceId.GOOGLE_CALENDAR} />
                        </div>
                      ) : null}
                      <SelectDropdown
                        className='zocks-px-0.75 py-2'
                        initialSelectedOption={selectedCalendarIntegration ? calendarIntegrationOptionsByIntegration[selectedCalendarIntegration] : null}
                        placeholder={'Choose calendar'}
                        options={calendarIntegrationOptions}
                        onOptionSelected={(selected) => setSelectedCalendarIntegration(selected.id as IntegrationServiceId)}
                        disabled={isCalendarIntegrationFinished}
                      />
                      <Button
                        variant='primary'
                        onClick={() => handleCalendarIntegration(selectedCalendarIntegration)}
                        disabled={isCalendarIntegrationFinished || selectedCalendarIntegration == null}
                      >
                        Connect
                      </Button>
                    </Stack>
                    {!finishedSteps[1] && (
                      <Button
                        variant='link'
                        onClick={() => {
                          dismissOnboardingItem('calendar-integration', OnboardingTrackEvent.ONBOARDING_CALENDAR_INTEGRATION_DISMISSED, 2)
                        }}
                      >
                        Skip for now
                      </Button>
                    )}
                  </Stack>
                </StepItemAccordion>
              ) : null}
              {isIntegrationAllowed(IntegrationServiceId.ZOOM) ? (
                <StepItemAccordion headerText='Automatically process your meetings' eventKey='2' finished={finishedSteps[2]}>
                  <div>By connecting your meeting providers, Zocks will automatically capture meeting results.</div>
                  <Stack direction='horizontal' gap={3} className='my-4 justify-content-between'>
                    <Button disabled={zoomIntegration} variant='primary' onClick={handleZoomIntegration}>
                      <i className='bi bi-camera-video' /> Connect Zoom
                    </Button>

                    {!finishedSteps[2] && (
                      <Button
                        variant='link'
                        onClick={() => dismissOnboardingItem('zoom-integration', OnboardingTrackEvent.ONBOARDING_ZOOM_INTEGRATION_DISMISSED, 3)}
                      >
                        Skip for now
                      </Button>
                    )}
                  </Stack>
                </StepItemAccordion>
              ) : null}
              <StepItemAccordion headerText='Spend 5 minutes learning how to use Zocks' eventKey='3' finished={finishedSteps[3]}>
                <div>
                  Get started with Zocks: A quick 5-minute overview to get you oriented.
                  <br />
                  Curious to learn more? Check out our in-depth walkthrough below!
                </div>
                <Stack gap={3} direction='horizontal' className='my-4 justify-content-between'>
                  <ArcadeEmbed
                    url='https://demo.arcade.software/Bagly0QoO46MkIgEcO42?embed&embed_mobile=tab&embed_desktop=inline&show_copy_link=true'
                    title='Zocks overview'
                    onArcadeStarted={handleViewDemoMeeting}
                  />
                </Stack>
              </StepItemAccordion>
            </Accordion>
          </Col>
        </Row>
        <Row className='mt-8'>
          <Col>
            <Stack direction='horizontal' gap={3} className='zocks-bg-color-primary-light justify-content-center py-4'>
              <div>Are you ready to use Zocks?</div>
              {!isFinishedGettingStarted && !finishedSteps.every((step) => step) ? (
                <Button variant='link' onClick={onDismissAll}>
                  Skip these steps and start using Zocks
                </Button>
              ) : (
                <Button variant='link' onClick={() => navigate('/workspace/past-meetings')}>
                  Let's go!
                </Button>
              )}
            </Stack>
          </Col>
        </Row>
      </Container>
    </>
  ) : (
    <></>
  )
}
